<template>
  <div id="app">
    <Navbar />
    <notifications group="notification" position="top center" classes="notification-style" />
    <router-view />
  </div>
</template>

<script>
import Navbar from "@/components/Navbar";

export default {
  components: {
    Navbar
  },
}
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap");
@import url("https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap");

:root {
  font-size: 10px;

  /* Brand */
  --primary: #4F46E5;
  --primary-hover: #4338CA;
  --primary-light: #EEF2FF;
  --primary-50: #EEF2FF;
  --primary-100: #E0E7FF;
  --primary-500: #4F46E5;
  --primary-600: #4338CA;
  --primary-700: #3730A3;

  /* Neutral */
  --gray-50: #F9FAFB;
  --gray-100: #F3F4F6;
  --gray-200: #E5E7EB;
  --gray-300: #D1D5DB;
  --gray-400: #9CA3AF;
  --gray-500: #6B7280;
  --gray-600: #4B5563;
  --gray-700: #374151;
  --gray-800: #1F2937;
  --gray-900: #111827;

  /* Semantic */
  --success: #10B981;
  --success-light: #D1FAE5;
  --warning: #F59E0B;
  --warning-light: #FEF3C7;
  --danger: #EF4444;
  --danger-light: #FEE2E2;
  --info: #3B82F6;
  --info-light: #DBEAFE;

  /* Surfaces */
  --bg: #F5F6FA;
  --card: #FFFFFF;
  --border: #E5E7EB;
  --border-light: #F3F4F6;

  /* Text */
  --text-primary: #111827;
  --text-secondary: #6B7280;
  --text-tertiary: #9CA3AF;
  --text-inverse: #FFFFFF;

  /* Legacy compat */
  --aurizor-blue: #4F46E5;
  --black: #111827;
  --red-btn-text: #DC2626;
  --red-btn-bg: #FEE2E2;

  /* Shadows */
  --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.06), 0 2px 4px -1px rgba(0, 0, 0, 0.04);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.07), 0 4px 6px -2px rgba(0, 0, 0, 0.04);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.07), 0 10px 10px -5px rgba(0, 0, 0, 0.03);

  /* Radius */
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 20px;
  --radius-full: 9999px;

  /* Transition */
  --transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html {
  scroll-behavior: smooth;
}

body {
  background: var(--bg);
  color: var(--text-primary);
}

button {
  border: none;
  outline: none;
  cursor: pointer;
  font-weight: 500;
  font-family: inherit;
  border-radius: var(--radius-sm);
  transition: all var(--transition);
}

h1, h2, h3, h4, h5, h6, p {
  color: var(--text-primary);
}

h4 {
  font-size: 32px;
  font-weight: 600;
  letter-spacing: -0.02em;
}

h6 {
  font-weight: 600;
  font-size: 17px;
  line-height: 1.5;
}

h5 {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.4;
}

a {
  text-decoration: none;
  color: var(--primary);
  transition: color var(--transition);

  &:hover {
    color: var(--primary-hover);
  }
}

code, .mono {
  font-family: 'JetBrains Mono', monospace;
}

.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.container-small {
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 0 24px;
}

.sub-title {
  font-size: 16px;
  line-height: 1.5;
  color: var(--text-secondary);
}

.sub-title-two {
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-secondary);
}

.input {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  .input-error {
    font-size: 13px;
    color: var(--danger) !important;
    font-weight: 500;
    margin-top: 6px;
  }

  label {
    font-size: 14px;
    margin-bottom: 6px;
    font-weight: 500;
    color: var(--text-primary);
  }

  input, textarea {
    padding: 10px 14px;
    font-size: 15px;
    font-weight: 500;
    color: var(--text-primary);
    vertical-align: middle;
    border: 1.5px solid var(--border);
    box-sizing: border-box;
    border-radius: var(--radius-sm);
    width: 100%;
    background: var(--card);
    line-height: 1.5;
    outline: none;
    resize: none;
    font-family: inherit;
    transition: all var(--transition);

    &::placeholder {
      color: var(--text-tertiary);
      font-weight: 400;
    }

    &:hover {
      border-color: var(--gray-300);
    }

    &:active, &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px var(--primary-50);
      outline: none;
    }
  }
}

.primary-btn {
  color: var(--text-inverse);
  background: var(--primary);
  font-weight: 600;

  &:hover {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  &:active {
    transform: translateY(0);
  }
}

.btn-danger {
  color: var(--danger);
  background: var(--danger-light);
  font-weight: 600;

  &:hover {
    background: #FECACA;
  }
}

.disable-click {
  pointer-events: none;
  cursor: default;
  opacity: 0.6;
}

// Button loader
.loader {
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 3px solid #fff;
  width: 18px;
  height: 18px;
  animation: spin 0.8s linear infinite;
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
}
.ld-bg {
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  border-radius: var(--radius-sm);
  pointer-events: none;
  backdrop-filter: blur(2px);
}
.btn-done-status {
  width: 100%;
  height: 100%;
  background: var(--success);
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  border-radius: var(--radius-sm);
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
  visibility: hidden;
  opacity: 0;
  cursor: default;
  transition: all var(--transition);
}
.makeVisible {
  visibility: visible !important;
  opacity: 1 !important;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Toast
.toasted .primary, .toasted.toasted-primary {
  border-radius: var(--radius-sm) !important;
  background-color: var(--gray-900) !important;
  padding: 12px 20px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  box-shadow: var(--shadow-lg) !important;
  font-family: 'Inter', sans-serif !important;
}

// Scrollbar
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: var(--gray-300);
  border-radius: 3px;
  &:hover {
    background: var(--gray-400);
  }
}

/* Global dropdown transition (used by Navbar + DashHome) */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
